import {
  useEffect,
  useRef,
  useState,
  forwardRef,
  useImperativeHandle,
} from "react";
import { classNames } from "primereact/utils";
import { Icon } from "../Icon";

import styles from "./CollapsibleBlock.module.css";

export interface CollapsibleProps {
  title: string;
  children: React.ReactNode;
  visibility?: boolean;
  withScroll?: boolean;
  className?: string;
  onClick?: () => void;
}

export interface CollapsibleBlockActions {
  open: () => void;
  close: () => void;
}

const CollapsibleBlock = forwardRef<CollapsibleBlockActions, CollapsibleProps>(
  (
    { title, children, visibility = false, withScroll, className, onClick },
    ref
  ) => {
    const [show, setShow] = useState<boolean>(false);
    const collapseRef = useRef<HTMLButtonElement>(null);

    const handleClick = (e: any) => {
      e.preventDefault();
      setShow((prev) => !prev);
      onClick?.();
    };

    useImperativeHandle(ref, () => ({
      open: () => setShow(true),
      close: () => setShow(false),
    }));

    useEffect(() => {
      setShow(visibility);
    }, [visibility]);

    useEffect(() => {
      if (show && withScroll)
        collapseRef?.current?.scrollIntoView({
          block: "start",
          behavior: "smooth",
        });
    }, [show, withScroll]);

    return (
      <>
        <button
          type="button"
          ref={collapseRef}
          className={classNames(styles.container, className)}
          onClick={handleClick}
          onKeyDown={(e) => {
            if (e.code === "Enter" || e.code === "Space") {
              handleClick(e);
            }
          }}
        >
          <div className={styles.titleContainer}>
            <span className={styles.title}>{title}</span>
            {show ? (
              <Icon name="chevron-up" color="Primary-700" size={18} />
            ) : (
              <Icon name="chevron-down" color="Primary-700" size={18} />
            )}
          </div>
        </button>
        <div
          className={classNames({
            [styles.hidden]: !show,
            ["w-100"]: true,
          })}
        >
          {children}
        </div>
      </>
    );
  }
);

export default CollapsibleBlock;
